import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Layers } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 0-100 → HSL ısı rengi. 0 = kırmızı, 50 = sarı, 100 = yeşil.
// Tailwind/theme tonlarıyla bilinçli olarak çakışmıyor — heatmap'in tek
// görsel dili olsun.
function pctToColor(pct, totalUsers) {
  if (!totalUsers) return 'hsl(220, 8%, 30%)'; // veri yok → nötr gri
  const hue = (pct / 100) * 120; // 0=kırmızı, 120=yeşil
  return `hsl(${hue}, 70%, 35%)`;
}

/**
 * Departman × modül tamamlanma yüzdesi grid'i.
 * Çok fazla modül olabilir (50+); yatay scroll ile geniş görünüm.
 *
 * Veriyi kendi çeker (auditor/compliance_officer/tenant_admin/super_admin
 * erişimine sahip rollerden çağrılmalı).
 */
export default function ComplianceHeatmap({ className = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/auditor/compliance-heatmap`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        if (!cancelled) setData(body);
      } catch (err) {
        if (!cancelled) toast.error('Uyum ısı haritası yüklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center text-muted-foreground text-sm">
          Yükleniyor...
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.departments?.length || !data.modules?.length) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center text-muted-foreground text-sm">
          Veri yok.
        </CardContent>
      </Card>
    );
  }

  // Lookup: department -> module_id -> cell
  const lookup = {};
  for (const c of data.cells) {
    lookup[c.department] = lookup[c.department] || {};
    lookup[c.department][c.module_id] = c;
  }

  return (
    <Card className={className} data-testid="compliance-heatmap">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <Layers className="w-4 h-4" />
          Departman × Modül Uyum Isı Haritası
          <Badge variant="outline" className="ml-2 text-[10px]">
            {data.departments.length} dep · {data.modules.length} modül
          </Badge>
          <div className="ml-auto flex items-center gap-2 text-[10px] text-muted-foreground">
            <span className="w-3 h-3 rounded" style={{ background: pctToColor(0, 1) }} />
            <span>0%</span>
            <span className="w-3 h-3 rounded" style={{ background: pctToColor(50, 1) }} />
            <span>50%</span>
            <span className="w-3 h-3 rounded" style={{ background: pctToColor(100, 1) }} />
            <span>100%</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="border-separate border-spacing-0.5 text-[10px]">
            <thead>
              <tr>
                <th className="text-left p-1 sticky left-0 bg-background z-10 min-w-[110px]"></th>
                {data.modules.map(m => (
                  <th key={m.id} className="p-1 text-muted-foreground font-mono w-7" title={m.name}>
                    {m.id.replace('M', '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.departments.map(dep => (
                <tr key={dep}>
                  <td className="p-1 font-mono sticky left-0 bg-background z-10 text-foreground/80">
                    {dep}
                  </td>
                  {data.modules.map(m => {
                    const c = lookup[dep]?.[m.id];
                    if (!c) {
                      return <td key={m.id} className="w-7 h-7 rounded bg-muted/20" />;
                    }
                    return (
                      <td
                        key={m.id}
                        className="w-7 h-7 rounded text-center text-white/90 font-medium cursor-help"
                        style={{ background: pctToColor(c.completion_pct, c.total_users) }}
                        title={`${dep} × ${m.name}\n${c.completed_users}/${c.total_users} kullanıcı (${c.completion_pct}%)`}
                      >
                        {c.total_users === 0 ? '–' : Math.round(c.completion_pct)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">
          Sayılar her hücredeki tamamlanma yüzdesidir. Üzerine gelince detay görünür.
        </p>
      </CardContent>
    </Card>
  );
}
